import React, { useMemo, useState } from 'react';
import { arc, pie } from 'd3-shape';
import { useTheme } from '../../../contexts/ThemeContext';
import { getChartTheme } from '../charting/chartTheme';
import { buildThreatCategoryData } from './detectionTypeUtils';

const VIEWBOX_SIZE = 320;
const CHART_CENTER = VIEWBOX_SIZE / 2;
const INNER_RADIUS = 86;
const OUTER_RADIUS = 136;
const CENTER_FONT_SIZE = 16;
const CENTER_LINE_GAP = 22;
const HOVER_RADIUS = 10;

const Chart = ({ site }) => {
  const { isDarkMode } = useTheme();
  const theme = getChartTheme(isDarkMode);
  const data = useMemo(() => buildThreatCategoryData(site.data, theme), [site.data, theme]);
  const [activeIndex, setActiveIndex] = useState(null);

  const chartData = data.length > 0
    ? data
    : [{ name: 'No Data', value: 100, color: theme.strokeLow, isEmpty: true }];

  const pieGenerator = pie().sort(null).value((item) => item.value);
  const slices = pieGenerator(chartData);

  return (
    <div className="donut chart">
      <svg
        className="donut__svg"
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
        role="img"
        aria-label="Threat categories donut chart"
      >
        <g transform={`translate(${CHART_CENTER}, ${CHART_CENTER})`}>
          {slices.map((slice, index) => {
            const isActive = activeIndex === index;
            const sliceArc = arc()
              .innerRadius(INNER_RADIUS)
              .outerRadius(isActive ? OUTER_RADIUS + HOVER_RADIUS : OUTER_RADIUS)
              .cornerRadius(3);

            return (
              <path
                key={slice.data.name}
                d={sliceArc(slice) || ''}
                fill={slice.data.color}
                stroke={theme.surfaceLow}
                strokeWidth={3}
                opacity={slice.data.isEmpty ? 0.45 : 1}
                style={{
                  cursor: slice.data.isEmpty ? 'default' : 'pointer',
                  filter: isActive ? 'brightness(1.08)' : 'none',
                }}
                onMouseEnter={() => {
                  if (!slice.data.isEmpty) setActiveIndex(index);
                }}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {!slice.data.isEmpty ? (
                  <title>{`${slice.data.name}: ${slice.data.value.toFixed(0)}%`}</title>
                ) : null}
              </path>
            );
          })}

          <text
            textAnchor="middle"
            dominantBaseline="middle"
            x="0"
            y="0"
            style={{
              fontSize: `${CENTER_FONT_SIZE}px`,
              fontWeight: 700,
              fill: theme.textHigh,
            }}
          >
            <tspan x="0" dy={-CENTER_LINE_GAP * 0.35}>THREAT</tspan>
            <tspan x="0" dy={CENTER_LINE_GAP * 0.65}>CATEGORIES</tspan>
          </text>
        </g>
      </svg>

      {data.length > 0 ? (
        <div className="donut__legend">
          {data.map((item, index) => (
            <div
              key={item.name}
              className={`donut__item${activeIndex === index ? ' is-active' : activeIndex !== null ? ' is-muted' : ''}`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <span
                className="donut__swatch"
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              />
              <span className="donut__value">{item.value.toFixed(0)}%</span>
              <span className="donut__label">{item.name}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state donut__empty">No threat categories available.</div>
      )}
    </div>
  );
};

export default Chart;
